import React from "react";
import { InView, LineReveal } from "../motion/Reveal";
import { mediaUrl } from "@/lib/api";
import { optimizeUrl, buildSrcSet } from "@/lib/img";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang, UI } from "@/lib/lang";

export default function Services({ services = [] }) {
  const { t } = useLang();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-20 md:py-40 bg-[#0A0A0A]"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 md:px-14">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <p className="num-tick mb-4">{t(UI.section02)}</p>
          </div>
          <div className="col-span-12 md:col-span-9">
            <h2
              className="font-display uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.9]"
              data-testid="services-title"
            >
              <LineReveal text={t(UI.servicesH1)} delay={0.1} />
              <span className="block txt-stroke">
                <LineReveal text={t(UI.servicesH2)} delay={0.35} />
              </span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-12 auto-rows-[220px] md:auto-rows-[260px] gap-3 md:gap-4">
          {services.map((s, i) => {
            const layouts = [
              "col-span-12 md:col-span-7 row-span-2",
              "col-span-12 md:col-span-5 row-span-1",
              "col-span-6 md:col-span-5 row-span-1",
              "col-span-6 md:col-span-4 row-span-2",
              "col-span-12 md:col-span-8 row-span-1",
              "col-span-12 md:col-span-12 row-span-1",
            ];
            return (
              <ServiceCard key={s.id} service={s} className={layouts[i % layouts.length]} index={i} />
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, className, index }) {
  const { t } = useLang();
  return (
    <InView
      y={40}
      delay={0.05 * (index % 6)}
      className={`group relative overflow-hidden card-sharp ${className}`}
    >
      <a
        href="#contact"
        className="block relative w-full h-full"
        data-testid={`service-card-${service.id}`}
      >
        <motion.div
          className="absolute inset-0"
          whileHover={{ scale: 1.06 }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src={optimizeUrl(mediaUrl(service.image), 1200)}
            srcSet={buildSrcSet(mediaUrl(service.image))}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 40vw"
            alt={t(service.title)}
            className="w-full h-full object-cover"
            loading={index < 2 ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={index < 2 ? "high" : "auto"}
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

        <div className="relative z-10 h-full p-6 md:p-8 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <span className="num-tick text-[#1E7FFF]">{service.num}</span>
            <ArrowUpRight
              className="text-white/40 group-hover:text-[#1E7FFF] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all"
              size={22}
            />
          </div>
          <div>
            <h3 className="font-display uppercase text-white text-3xl md:text-4xl lg:text-5xl leading-[0.95] mb-2">
              {t(service.title)}
            </h3>
            {service.sub && <p className="num-tick mb-3 text-white/50">{t(service.sub)}</p>}
            <p className="max-w-md text-white/70 text-sm md:text-base font-body leading-relaxed">
              {t(service.desc)}
            </p>
          </div>
        </div>

        <div className="corner-ticks opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </a>
    </InView>
  );
}
